import { useState, useEffect, forwardRef, useImperativeHandle, type ReactNode } from "react";
import { Wand2, Eraser, Info, X } from "lucide-react";
import { Button } from "@/shared/components/ui/button";
import { useToast } from "@/shared/hooks/use-toast";
import { requestMagicFill, type MagicFillResult } from "./magicFillRequest";
import { canUseMagicFill } from "./canUseMagicFill";
import { useAuth } from "@/features/Auth/state/AuthContext";
import { useTranslation } from "react-i18next";
import type { FormFieldsMetadata } from "@/shared/types/magicFillTypes";
import { useMagicFill } from "@/shared/contexts/MagicFillContext";

export type MagicFillFormType =
  | "organization_onboarding"
  | "organization_admin"
  | "product"
  | "knowledge_import";

export interface MagicFillMetadata {
  organizationId?: string;
  productId?: string;
  workspaceId?: string;
  knowledgeBaseId?: string;
  fieldKey?: string;
  fieldOptions?: string[];
  fieldMetadata?: FormFieldsMetadata;
  onboarding?: boolean;
  /**
   * Quando o formulário envolve upload de arquivo, o caller pode passar
   * o nome/tipo/tamanho do arquivo aqui pra ser usado como contexto adicional
   * na IA (ex: "Prodgy Assistant Requirements.pdf" é sinal forte de
   * `type=requirement`).
   */
  sourceFile?: {
    name: string;
    type?: string;
    size?: number;
    /** Objeto File real — quando presente, o conteúdo é enviado ao backend para extração e análise pelo LLM. */
    file?: File;
  };
}

interface MagicFillButtonProps {
  formType: MagicFillFormType;
  /**
   * Função que retorna o snapshot atual dos valores do formulário
   */
  getCurrentValues: () => Record<string, unknown>;
  /**
   * Callback chamado com os campos sugeridos pela IA
   */
  onApplySuggestions: (suggested: Record<string, unknown>) => void;
  /**
   * Callback chamado quando o estado de loading muda
   */
  onLoadingChange?: (isLoading: boolean) => void;
  metadata?: MagicFillMetadata;
  className?: string;
  size?: "sm" | "default" | "icon";
  /**
   * Mostra botão de limpar formulário ao lado do Magic Fill
   */
  showClearButton?: boolean;
  /**
   * Callback chamado quando o botão limpar é clicado
   */
  onClear?: () => void;
  /**
   * Mostra popup/hint informativo
   */
  showHint?: boolean;
  /**
   * Texto customizado para o hint (se não informado, usa tradução padrão)
   */
  hintText?: ReactNode;
  /**
   * Callback chamado quando o hint fecha automaticamente após 10 segundos
   */
  onHintAutoClose?: () => void;
}

export interface MagicFillButtonHandle {
  triggerMagicFill: () => void;
}

export const MagicFillButton = forwardRef<MagicFillButtonHandle, MagicFillButtonProps>(({
  formType,
  getCurrentValues,
  onApplySuggestions,
  onLoadingChange,
  metadata,
  className,
  size = "sm",
  showClearButton = false,
  onClear,
  showHint = false,
  hintText,
  onHintAutoClose,
}, ref) => {
  const { toast } = useToast();
  const [isLoading, setIsLoading] = useState(false);
  const [isHintVisible, setIsHintVisible] = useState(showHint);
  const [isHintFadingOut, setIsHintFadingOut] = useState(false);
  const { t } = useTranslation();
  const { setGlobalLoading } = useMagicFill();

  const updateLoadingState = (loading: boolean) => {
    setIsLoading(loading);
    setGlobalLoading(loading); // Atualiza estado global para todos os campos
    onLoadingChange?.(loading);
  };

  // Auto-close hint após 10 segundos
  useEffect(() => {
    if (showHint) {
      setIsHintVisible(true);
      setIsHintFadingOut(false);
      
      const timer = setTimeout(() => {
        // Iniciar fade-out
        setIsHintFadingOut(true);
        
        // Após animação de fade-out (500ms), esconder completamente
        setTimeout(() => {
          setIsHintVisible(false);
          onHintAutoClose?.();
        }, 500);
      }, 7000); // 7 segundos

      return () => clearTimeout(timer);
    } else {
      setIsHintVisible(false);
      setIsHintFadingOut(false);
    }
  }, [showHint, onHintAutoClose]);
  const { user } = useAuth();
  const { i18n } = useTranslation();

  const isEnglish = i18n.language?.startsWith("en");

  if (!canUseMagicFill(formType, user)) {
    return null;
  }

  const TIMEOUT_MS = 10000;
  const MAX_RETRIES = 2;

  const executeRequest = (
    fields: Record<string, unknown>,
    signal: AbortSignal,
    file?: File
  ): Promise<MagicFillResult> =>
    requestMagicFill({
      formType,
      fields,
      metadata,
      file,
      signal,
      language: i18n.language || "pt-BR",
    });

  const handleClick = async () => {
    updateLoadingState(true);
    let lastError: Error | null = null;

    const currentValues = getCurrentValues() || {};
    // Exclude file/image fields — only send text fields to the LLM
    const textFields = Object.fromEntries(
      Object.entries(currentValues).filter(([key, value]) =>
        !key.toLowerCase().includes('logo') &&
        !key.toLowerCase().includes('avatar') &&
        !key.toLowerCase().includes('image') &&
        !(typeof value === 'string' && (value.startsWith('data:') || value.startsWith('blob:')))
      )
    );
    const file = metadata?.sourceFile?.file;

    for (let attempt = 0; attempt <= MAX_RETRIES; attempt++) {
      const abortController = new AbortController();
      const timeoutId = window.setTimeout(() => {
        abortController.abort();
      }, TIMEOUT_MS);

      try {
        const response = await executeRequest(textFields, abortController.signal, file);

        window.clearTimeout(timeoutId);

        if (!response?.suggestedFields) {
          toast({
            variant: "destructive",
            title: isEnglish
              ? "Unable to suggest auto-fill"
              : "Não foi possível sugerir preenchimento",
            description: isEnglish
              ? "Please try again in a few moments."
              : "Tente novamente em alguns instantes.",
          });
          updateLoadingState(false);
          return;
        }

        const suggested = response.suggestedFields;
        const updates: Record<string, unknown> = {};

        Object.entries(suggested).forEach(([key, value]) => {
          const current = currentValues[key];
          const currentIsEmpty =
            current === undefined ||
            current === null ||
            (typeof current === "string" && current.trim().length === 0);

          const suggestedIsValid =
            value !== undefined && value !== null && value !== "";

          if (suggestedIsValid) {
            if (currentIsEmpty) {
              updates[key] = value;
            } else if (typeof value === "string" && typeof current === "string") {
              const suggestedLower = value.toLowerCase().trim();
              const currentLower = current.toLowerCase().trim();
              if (
                suggestedLower !== currentLower &&
                suggestedLower.length > currentLower.length
              ) {
                updates[key] = value;
              }
            }
          }
        });

        if (Object.keys(updates).length === 0) {
          toast({
            title: isEnglish ? "Nothing to update" : "Nada para atualizar",
            description: isEnglish
              ? "AI could not find improvements for the current fields."
              : "A IA não encontrou melhorias para os campos atuais.",
          });
          updateLoadingState(false);
          return;
        }

        onApplySuggestions(updates);

        // toast({
        //   title: isEnglish ? "Suggestions applied" : "Sugestões aplicadas",
        //   description: isEnglish
        //     ? "Fields were filled and/or enriched. Review before saving."
        //     : "Campos foram preenchidos e/ou enriquecidos. Revise antes de salvar.",
        // });
        updateLoadingState(false);
        return;
      } catch (error: unknown) {
        window.clearTimeout(timeoutId);
        lastError = error instanceof Error ? error : new Error(String(error));

        if (lastError?.name === "AbortError" && attempt < MAX_RETRIES) {
          continue;
        }

        if (lastError?.name !== "AbortError") {
          break;
        }
      }
    }

    if (lastError?.name === "AbortError") {
      toast({
        variant: "destructive",
        title: isEnglish ? "Timeout exceeded" : "Tempo limite excedido",
        description: isEnglish
          ? "Auto-fill took too long after multiple attempts. Please try again later."
          : "O preenchimento automático demorou muito após várias tentativas. Tente novamente mais tarde.",
      });
    } else if (lastError) {
      const message =
        lastError?.message ||
        (isEnglish
          ? "Error while requesting auto-fill."
          : "Erro ao consultar o preenchimento automático.");

      toast({
        variant: "destructive",
        title: isEnglish
          ? "Error in auto-fill"
          : "Erro no preenchimento automático",
        description: message,
      });
    }

    updateLoadingState(false);
  };

  // Expor método para trigger programático
  // eslint-disable-next-line react-hooks/rules-of-hooks
  useImperativeHandle(ref, () => ({
    triggerMagicFill: handleClick,
  }));

  // Se showClearButton ou showHint estiver ativo, renderiza wrapper com botões e hint
  if (showClearButton || showHint) {
    return (
      <div className="relative">
        <div className="flex items-center gap-2">
          {/* Botão Magic Fill */}
          <Button
            type="button"
            variant="outline"
            size={size}
            onClick={handleClick}
            disabled={isLoading}
            className={className}
          >
            <Wand2 className={size === "icon" ? "h-4 w-4" : "mr-2 h-4 w-4"} />
            {size !== "icon" && (
              <>
                {isLoading
                  ? isEnglish
                    ? "Filling..."
                    : "Preenchendo..."
                  : isEnglish
                  ? "Fill with AI"
                  : "Preencher com IA"}
              </>
            )}
          </Button>

          {/* Botão Limpar (opcional) */}
          {showClearButton && onClear && (
            <Button
              type="button"
              variant="ghost"
              size="icon"
              onClick={onClear}
              disabled={isLoading}
              className="h-8 w-8 hover:bg-muted"
              title={isEnglish ? "Clear form" : "Limpar formulário"}
            >
              <Eraser className="h-4 w-4" />
            </Button>
          )}
        </div>

        {/* Popup Hint (opcional) */}
        {isHintVisible && (
          <div 
            className="absolute top-10 right-0 z-10"
            style={{
              animation: isHintFadingOut 
                ? 'popupExit 500ms ease-out forwards' 
                : 'popupEntrance 1.2s cubic-bezier(0.34, 1.56, 0.64, 1) forwards'
            }}
          >
            <style>
              {`
                @keyframes popupEntrance {
                  0% {
                    opacity: 0;
                    transform: translateY(30px) scale(0.95);
                  }
                  60% {
                    opacity: 1;
                    transform: translateY(0) scale(1);
                  }
                  70% {
                    transform: translateY(0) scale(1) rotate(-2deg);
                  }
                  80% {
                    transform: translateY(0) scale(1) rotate(2deg);
                  }
                  90% {
                    transform: translateY(0) scale(1) rotate(-1deg);
                  }
                  100% {
                    opacity: 1;
                    transform: translateY(0) scale(1) rotate(0deg);
                  }
                }
                
                @keyframes popupExit {
                  0% {
                    opacity: 1;
                    transform: translateY(0) scale(1);
                  }
                  100% {
                    opacity: 0;
                    transform: translateY(-5px) scale(0.98);
                  }
                }
              `}
            </style>
            {/* Seta apontando para o magic button */}
            <div className="absolute -top-2 right-[52px] w-0 h-0 border-l-[8px] border-l-transparent border-r-[8px] border-r-transparent border-b-[8px] border-b-blue-200 dark:border-b-blue-800"></div>
            <div className="absolute -top-[7px] right-[52px] w-0 h-0 border-l-[8px] border-l-transparent border-r-[8px] border-r-transparent border-b-[8px] border-b-blue-50 dark:border-b-blue-950/50"></div>
            
            <div className="bg-blue-50 dark:bg-blue-950/50 border border-blue-200 dark:border-blue-800 rounded-lg p-4 pr-7 shadow-lg w-[260px] backdrop-blur-sm relative">
              <button
                type="button"
                onClick={() => {
                  setIsHintFadingOut(true);
                  setTimeout(() => {
                    setIsHintVisible(false);
                    onHintAutoClose?.();
                  }, 300);
                }}
                aria-label={t('common.actions.close')}
                className="absolute top-1.5 right-1.5 p-1 rounded hover:bg-blue-100 dark:hover:bg-blue-900/50 text-blue-500 dark:text-blue-400 transition-colors"
              >
                <X className="h-3 w-3" />
              </button>
              <div className="flex items-start gap-2">
                <Info className="h-3.5 w-3.5 text-blue-500 dark:text-blue-400 flex-shrink-0 mt-0.5" />
                <div className="text-xs text-blue-900 dark:text-blue-100 leading-relaxed">
                  {hintText ?? (
                    <>
                      <span className="font-semibold">
                        {t("auth.onboarding.organization.testingPrompt").split("?")[0]}?
                      </span>
                      <br />
                      {t("auth.onboarding.organization.testingPrompt").split("?")[1]}
                    </>
                  )}
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    );
  }

  // Renderização simples quando não há botão limpar nem hint
  return (
    <Button
      type="button"
      variant="outline"
      size={size}
      onClick={handleClick}
      disabled={isLoading}
      className={className}
    >
      <Wand2 className={size === "icon" ? "h-4 w-4" : "mr-2 h-4 w-4"} />
      {size !== "icon" && (
        <>
          {isLoading
            ? isEnglish
              ? "Filling..."
              : "Preenchendo..."
            : isEnglish
            ? "Fill with AI"
            : "Preencher com IA"}
        </>
      )}
    </Button>
  );
});
